import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Col, Form, ListGroup, Modal, Row, Spinner } from "react-bootstrap";
import { CameraVideoFill, PencilSquare, PlayCircleFill, PlusLg } from "react-bootstrap-icons";
import { apiGet, getMediaFileUrl, isVideoCategory, MEDIA_CATEGORIES } from "./contentApi";
import MediaItemFormModal from "./MediaItemFormModal";
import MediaFilesCheckModal from "./MediaFilesCheckModal";

const MediaPlayerModal = ({ item, onClose }) => {
    const [src, setSrc] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!item) return;
        let cancelled = false;
        setSrc(null);
        setError("");

        if (!item.url) {
            setError("No file is attached to this item. Edit it to attach one.");
            return;
        }

        getMediaFileUrl(item.id)
            .then((url) => !cancelled && setSrc(url))
            .catch((err) => !cancelled && setError(err.message));

        return () => {
            cancelled = true;
        };
    }, [item]);

    if (!item) return null;
    const isVideo = isVideoCategory(item.category);

    return (
        <Modal show onHide={onClose} size={isVideo ? "lg" : undefined} centered>
            <Modal.Header closeButton>
                <Modal.Title className="fs-5" style={{ whiteSpace: "pre-wrap", tabSize: 4 }}>
                    {item.title}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {!src && !error && (
                    <div className="text-center py-3">
                        <Spinner animation="border" />
                    </div>
                )}
                {src && (isVideo ? (
                    <video src={src} controls autoPlay className="w-100 rounded" />
                ) : (
                    <audio src={src} controls autoPlay className="w-100" />
                ))}

                <div className="text-muted small mt-3">
                    {item.unitTitle && <>Unit {item.unitScreenOrder}: {item.unitTitle} · </>}
                    File: {item.url ? <code>{item.url}</code> : "none"}
                </div>

                {item.body && (
                    <div className="mt-3 p-3 rounded bg-body-tertiary" style={{ whiteSpace: "pre-wrap" }}>
                        {item.body}
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
};

const MediaItemsTab = ({ levels }) => {
    const [levelId, setLevelId] = useState("");
    const [category, setCategory] = useState(MEDIA_CATEGORIES[0].value);
    const [unitId, setUnitId] = useState("");
    const [units, setUnits] = useState([]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [playingItem, setPlayingItem] = useState(null);
    const [formState, setFormState] = useState({ show: false, item: null });
    const [showFilesCheck, setShowFilesCheck] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [notice, setNotice] = useState("");

    const formDefaults = useMemo(() => ({ levelId, category, unitId }), [levelId, category, unitId]);

    const showNotice = (message) => {
        setNotice(message);
        setTimeout(() => setNotice(""), 2500);
    };

    const handleSaved = (saved) => {
        setFormState({ show: false, item: null });
        setReloadKey((key) => key + 1);
        showNotice(`"${saved?.title ?? "Media item"}" saved`);
    };

    const rowActions = (item) => (
        <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            <Button
                size="sm"
                variant="outline-secondary"
                title="Edit"
                onClick={() => setFormState({ show: true, item })}
            >
                <PencilSquare />
            </Button>
        </div>
    );

    useEffect(() => {
        if (!levelId && levels.length > 0) setLevelId(String(levels[0].id));
    }, [levels, levelId]);

    useEffect(() => {
        if (!levelId) return;
        setUnitId("");
        apiGet(`/levels/${levelId}/units`)
            .then((data) => setUnits(data || []))
            .catch(() => setUnits([]));
    }, [levelId]);

    useEffect(() => {
        if (!levelId) return;
        let cancelled = false;
        setLoading(true);
        setError("");

        apiGet("/admin/content/media-items", { levelId, category, unitId })
            .then((data) => !cancelled && setItems(data || []))
            .catch((err) => {
                if (cancelled) return;
                setItems([]);
                setError(err.message);
            })
            .finally(() => !cancelled && setLoading(false));

        return () => {
            cancelled = true;
        };
    }, [levelId, category, unitId, reloadKey]);

    const playableCount = items.filter((item) => !item.isGroupTitle).length;

    return (
        <>
            <Row className="g-2 mb-3">
                <Col md={4}>
                    <Form.Label className="small text-muted mb-1">Level</Form.Label>
                    <Form.Select value={levelId} onChange={(e) => setLevelId(e.target.value)}>
                        {levels.map((level) => (
                            <option key={level.id} value={level.id}>{level.title}</option>
                        ))}
                    </Form.Select>
                </Col>
                <Col md={4}>
                    <Form.Label className="small text-muted mb-1">Type</Form.Label>
                    <Form.Select value={category} onChange={(e) => setCategory(e.target.value)}>
                        {MEDIA_CATEGORIES.map((c) => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                    </Form.Select>
                </Col>
                <Col md={4}>
                    <Form.Label className="small text-muted mb-1">Unit</Form.Label>
                    <Form.Select value={unitId} onChange={(e) => setUnitId(e.target.value)}>
                        <option value="">All units</option>
                        {units.map((unit) => (
                            <option key={unit.id} value={unit.id}>
                                {unit.screenOrder}. {unit.title}
                            </option>
                        ))}
                    </Form.Select>
                </Col>
            </Row>

            <div className="d-flex justify-content-end gap-2 mb-3">
                <Button variant="outline-secondary" onClick={() => setShowFilesCheck(true)}>
                    Check files
                </Button>
                <Button
                    variant="primary"
                    className="d-flex align-items-center gap-2"
                    onClick={() => setFormState({ show: true, item: null })}
                    disabled={levels.length === 0}
                >
                    <PlusLg /> Add media item
                </Button>
            </div>

            {notice && <Alert variant="success" className="py-2">{notice}</Alert>}
            {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}

            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" />
                </div>
            ) : items.length === 0 ? (
                <p className="text-muted text-center py-4">No media items for this selection.</p>
            ) : (
                <>
                    <div className="text-muted small mb-2">{playableCount} items · click an item to play it</div>
                    <ListGroup className="shadow-sm">
                        {items.map((item) =>
                            item.isGroupTitle ? (
                                <ListGroup.Item
                                    key={item.id}
                                    className="fw-bold bg-body-tertiary d-flex align-items-center gap-3"
                                >
                                    <span className="flex-grow-1">{item.title}</span>
                                    {rowActions(item)}
                                </ListGroup.Item>
                            ) : (
                                <ListGroup.Item
                                    key={item.id}
                                    as="div"
                                    action
                                    role="button"
                                    onClick={() => setPlayingItem(item)}
                                    className="d-flex align-items-center gap-3"
                                >
                                    {isVideoCategory(item.category) ? (
                                        <CameraVideoFill className="text-primary flex-shrink-0" size={22} />
                                    ) : (
                                        <PlayCircleFill className="text-primary flex-shrink-0" size={22} />
                                    )}
                                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                        <div className="text-truncate">{item.title}</div>
                                        {!unitId && item.unitTitle && (
                                            <div className="small text-muted">
                                                Unit {item.unitScreenOrder}: {item.unitTitle}
                                            </div>
                                        )}
                                    </div>
                                    {!item.url && <Badge bg="secondary">No file</Badge>}
                                    {item.isPremium && <Badge bg="warning" text="dark">Premium</Badge>}
                                    {rowActions(item)}
                                </ListGroup.Item>
                            )
                        )}
                    </ListGroup>
                </>
            )}

            <MediaPlayerModal item={playingItem} onClose={() => setPlayingItem(null)} />

            <MediaFilesCheckModal
                show={showFilesCheck}
                levels={levels}
                onClose={() => setShowFilesCheck(false)}
                onEditItem={(item) => {
                    setShowFilesCheck(false);
                    setFormState({ show: true, item });
                }}
            />

            <MediaItemFormModal
                show={formState.show}
                item={formState.item}
                defaults={formDefaults}
                levels={levels}
                onClose={() => setFormState({ show: false, item: null })}
                onSaved={handleSaved}
            />
        </>
    );
};

export default MediaItemsTab;
